import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Button, ButtonGroup, Col, Input, Row, Table } from "reactstrap";
import { ItemDropdownMenu, SortHeader } from ".";
import { api, constants, helpers } from "../utils";

export default function StockStatusGrid({ mode, criteria, ...props }) {
  const GridMode = mode;
  const Criteria = criteria;
  const [data, setData] = useState();
  const [editCopy, setEditCopy] = useState(null);
  const [editIndex, setEditIndex] = useState(null);
  const [locationTotal, setLocationTotal] = useState(0);
  const [total, setTotal] = useState(0);
  const [working, setWorking] = useState(false);
  let alreadyDisplayedList = [];
  const locationCols = Criteria?.locations.length > 0;

  useEffect(() => {
    if (data !== props.data) {
      setData(props.data);     
      getTotal();
      getLocationTotal();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.data]);

  function allowEdit(index) {
    setEditIndex(index);
    setEditCopy(_.assign({}, props.data[index]));
  }

  function cancelEdit() {
    setEditIndex(null);
    setEditCopy(null);
  }

  const getExcel = () => {
    setWorking(true);
    props.exportXlsxCallback();
    setWorking(false);
  };

  function getLocationTotal() {
    setLocationTotal(
      props.data.reduce(
        (total, currentItem) => (total += currentItem.siteCost),
        0
      )
    );
  }

  function getTotal() {
    let list = _.map(_.groupBy(props.data, x => x.itemNumber), y => y[0]);    
    setTotal(
      list.reduce(
        (total, currentItem) => (total += currentItem.totalCost),
        0
      )
    );
  }

  function handleChange(field, value) {
    const copyUpdate = _.assign({}, editCopy);
    copyUpdate[field] = value;
    setEditCopy(copyUpdate);
  }

  function updateItemDesc() {
    setWorking(true);
    const payload = _.chain({})
      .assign(editCopy)
      .pickBy((_v, k) => editCopy[k] !== props.data[editIndex][k])
      .value();

    if (_.keys(payload).length) {
      payload["itemNumber"] = editCopy.itemNumber;
      if (!payload.itemDescription?.length) {
        toast.error("provide valid description");
        setWorking(false);
        return;
      }
      if (payload.itemDescription?.length > 100) {
        toast.error("description truncated");
        setWorking(false);
        return;
      }

      api
        .post(`Inventory/UpdateItemDescription`, payload)
        .then((response) => {
          if (response.data.success) {
            props.onUpdateRow();
          } else {
            if (_.isString(response?.data?.message)) {
              toast.error(response?.data?.message);
            }
          }
        })
        .catch(api.catchHandler)
        .finally(() => {
          cancelEdit();
        });
    } else {
      cancelEdit();
    }
    setWorking(false);
  }

  return (
    <>
      <Row className="align-items-center">
        <Col className="transparent-background">
          <ButtonGroup className="float-end">
            {props.data && props.data.length && props.exportXlsxCallback && (
              <Button
                title="Download as .xlsx"
                className="shredButtonExcel float-end ms-2"
                onClick={getExcel}
                disabled={working}
              >
                <FontAwesomeIcon icon="file-excel" size="lg" />
              </Button>
            )}
          </ButtonGroup>
        </Col>
      </Row>
      <Table size="sm" className="data-table">
        <thead className="table-header">
          <tr>
            <th style={{ width: "12%" }}>
              <SortHeader
                displayName={"Item"}
                field={
                  GridMode === constants.StockStatusGridModes.edit
                    ? "ItemNumber"
                    : "ItemWithDescription"
                }
                sortDirection={props.sortDirection}
                sorted={
                  props.sortField ===
                  (GridMode === constants.StockStatusGridModes.edit
                    ? "ItemNumber"
                    : "ItemWithDescription")
                }
                callback={props.sortCallback}
              />
            </th>
            {GridMode === constants.StockStatusGridModes.edit ? (
              <th style={{ width: "25%" }}>
                <SortHeader
                  displayName={"Description"}
                  field="ItemDescription"
                  sortDirection={props.sortDirection}
                  sorted={props.sortField === "ItemDescription"}
                  callback={props.sortCallback}
                />
              </th>
            ) : null}
            <th style={{ width: "8%" }}>
              <SortHeader
                displayName="Class"
                field="ItemClass"
                sortDirection={props.sortDirection}
                sorted={props.sortField === "ItemClass"}
                callback={props.sortCallback}
              />
            </th>
            <th style={{ width: "5%" }}>
              <SortHeader
                displayName={<span>ABC <br /> Code</span>}
                field="ABCCode"
                sortDirection={props.sortDirection}
                sorted={props.sortField === "ABCCode"}
                callback={props.sortCallback}
              />
            </th>
            <th style={{ width: "7%" }}>
              <SortHeader
                displayName={"Vendor"}
                field="VendorId"
                sortDirection={props.sortDirection}
                sorted={props.sortField === "VendorId"}
                callback={props.sortCallback}
              />
            </th>
            {GridMode === constants.StockStatusGridModes.viewData ? (
              <>
                <th style={{ width: "8%" }}>
                  <SortHeader
                    displayName={locationCols ? (<span>Location</span>) : (<span>Total On<br />Order</span>)}
                    field={locationCols ? "SiteCode" : "TotalOrderQty"}
                    sortDirection={props.sortDirection}
                    sorted={locationCols ? props.sortField === "SiteCode" : props.sortField === "TotalOrderQty"}
                    callback={props.sortCallback}
                    textOnly={locationCols ? true : false}
                  />
                </th>
                <th style={{ width: "5%" }}>
                  <SortHeader
                    displayName={locationCols ? (<span>Bin</span>) : (<span>Total On<br />Hand</span>)}
                    field={locationCols ? "BinNumber" : "TotalOnHand"}
                    sortDirection={props.sortDirection}
                    sorted={locationCols ? props.sortField === "BinNumber" : props.sortField === "TotalOnHand"}
                    callback={props.sortCallback}
                    textOnly={locationCols ? true : false}
                  />
                </th>
                <th style={{ width: "5%" }}>
                  <SortHeader
                    displayName={
                      locationCols ? (<span>{" "}On<br />Order{" "}</span>) : (<span>Total<br />Allocated</span>)
                    }
                    field={locationCols ? "QtyOnOrder" : "TotalAllocated"}
                    sortDirection={props.sortDirection}
                    sorted={locationCols ? props.sortField === "QtyOnOrder" : props.sortField === "TotalAllocated"}
                    callback={props.sortCallback}
                  />
                </th>
                <th style={{ width: "5%" }}>
                  <SortHeader
                    displayName={
                      locationCols ? (<span>{" "}On<br />Hand{" "}</span>) : (<span>Total<br />Available</span>)
                    }
                    field={locationCols ? "QtyOnHand" : "TotalAvailable"}
                    sortDirection={props.sortDirection}
                    sorted={locationCols ? props.sortField === "QtyOnHand" : props.sortField === "TotalAvailable"}
                    callback={props.sortCallback}
                  />
                </th>
                {locationCols &&
                  <th>
                    <SortHeader
                       displayName="Allocated"
                       field="QtyAllocated"
                       sortDirection={props.sortDirection}
                       sorted={props.sortField === "QtyAllocated"}
                       callback={props.sortCallback}
                     />
                  </th>
                }
                {locationCols &&
                  <th>
                    <SortHeader
                       displayName="Available"
                       field="QtyAvailable"
                       sortDirection={props.sortDirection}
                       sorted={props.sortField === "QtyAvailable"}
                       callback={props.sortCallback}
                     />
                  </th>
                }


                <th style={{ width: "7%" }}>
                  <SortHeader
                    displayName={<span>Current<br />Cost</span>}
                    field="CurrentCost"
                    sortDirection={props.sortDirection}
                    sorted={props.sortField === "CurrentCost"}
                    callback={props.sortCallback}
                  />
                </th>
                {locationCols && (
                  <th style={{ width: "7%" }}>
                    <SortHeader
                      displayName={<span>Location<br />Value</span>}
                      field="SiteCost"
                      sortDirection={props.sortDirection}
                      sorted={props.sortField === "SiteCost"}
                      callback={props.sortCallback}
                    />
                  </th>
                )}
                <th style={{ width: "7%" }}>
                  <SortHeader
                    displayName={<span>Total<br />Value</span>}
                    field="TotalCost"
                    sortDirection={props.sortDirection}
                    sorted={props.sortField === "TotalCost"}
                    callback={props.sortCallback}
                  />
                </th>
              </>
            ) : (
              <>
                <th style={{ width: "5%" }}>
                  <SortHeader
                    displayName={"On Hand"}
                    field="TotalOnHand"
                    sortDirection={props.sortDirection}
                    sorted={props.sortField === "TotalOnHand"}
                    callback={props.sortCallback}
                  />
                </th>
                <th style={{ width: "5%" }}></th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {_.map(props.data, (datum, index) => {
            const alreadyDisplayed = _.includes(alreadyDisplayedList, datum.itemNumber);
            const isEditing = editIndex === index;
            const readOnly = !isEditing;
            alreadyDisplayedList.push(datum.itemNumber);
            if(alreadyDisplayed && !locationCols) // if no locations have been selected then it is a summaryView
            {return null} else {
            return (
              <tr
                key={index}
                className={`tr-${index % 2 === 0 ? "primary" : "secondary"}`}
              // style={{borderTop: alreadyDisplayed ? "inherit" : "2px solid black"}}
              >
                {GridMode === constants.StockStatusGridModes.edit ? (
                  <td className="data-cell" children={datum.itemNumber} />
                ) : locationCols ? (
                  alreadyDisplayed ? (
                    <td>{""}</td>
                  ) : (
                    <ItemDropdownMenu
                      itemNumber={datum.itemNumber}
                      itemDescription={datum.itemWithDescription}
                      attachmentCount={datum.itemAttachmentCount}
                    />
                  )
                ) : (
                  <ItemDropdownMenu
                    itemNumber={datum.itemNumber}
                    itemDescription={datum.itemWithDescription}
                    attachmentCount={datum.itemAttachmentCount}
                  />
                )}
                {GridMode === constants.StockStatusGridModes.edit ? (
                  <td>
                    {readOnly ? (
                      datum.itemDescription
                    ) : (
                      <Input
                        type="text"
                        bsSize="sm"
                        className={`px-1`}
                        value={editCopy.itemDescription}
                        onChange={(e) => handleChange("itemDescription", e.target.value)}
                      />
                    )}
                  </td>
                ) : null}
                <td className="data-cell">
                  {locationCols ? (
                     alreadyDisplayed ? ("") : (<span>{datum.itemClass}</span>)
                  ) : (
                    <span>{datum.itemClass}</span>
                  )}
                </td>
                <td>
                  {locationCols ? (
                    alreadyDisplayed ? ("") : (<span>{datum.abcDisplayCode}</span>)
                  ) : (
                    <span>{datum.abcDisplayCode}</span>
                  )}
                </td>
                <td className="data-cell" children={datum.vendorId} />
                {GridMode === constants.StockStatusGridModes.viewData ? (
                  <>
                    <td
                      className="data-cell"
                      children={locationCols ? datum.siteCode : Math.round(datum.totalOrderQty).toLocaleString()}
                    />
                    <td
                      className="data-cell"
                      children={locationCols ? datum.binNumber : Math.round(datum.totalOnHand).toLocaleString()}
                    />
                    <td
                      className="data-cell"
                           children={locationCols
                              ? datum.qtyOnOrder.toLocaleString()
                              : Math.round(datum.totalAllocated).toLocaleString()}
                    />
                    <td
                      className="data-cell"
                           children={locationCols
                              ? datum.qtyOnHand.toLocaleString()
                              : Math.round(datum.totalAvailable).toLocaleString()}
                    />
                    {locationCols && (
                      <td className="data-cell" children={datum.qtyAllocated.toLocaleString()} />
                    )}
                    {locationCols && (
                      <td className="data-cell" children={datum.qtyAvailable.toLocaleString()} />
                    )}
                    <td
                      className="data-cell"
                      children={helpers.formatCurrency(datum.currentCost)}
                    />
                    {locationCols && (
                      <td
                        className="data-cell"
                        children={helpers.formatCurrency(datum.siteCost)}
                      />
                    )}
                    <td>
                      {locationCols ? (
                        alreadyDisplayed ? (
                          ""
                        ) : (
                          <span>
                            {helpers.formatCurrency(datum.totalCost)}
                          </span>
                        )
                      ) : (
                        <span>
                          {helpers.formatCurrency(datum.totalCost)}
                        </span>
                      )}
                    </td>
                  </>
                ) : (
                  <>
                    <td className="data-cell" children={datum.qtyOnHand} />
                    {/* {canEdit && ( */}
                    <td className="data-cell">
                      <ButtonGroup className="width95px">
                        <Button
                          className={`icon-button me-1 ${isEditing ? "" : "float-end"
                            }`}
                          title={isEditing ? "Save changes" : "Edit line"}
                          color={isEditing ? "success" : "info"}
                          onClick={isEditing ? updateItemDesc : () => allowEdit(index)}
                          disabled={
                            (editIndex !== null && readOnly) ||
                            // validateEditCopy() ||
                            working
                          }
                        >
                          <FontAwesomeIcon
                            className="whiteIcon"
                            icon={isEditing ? "check" : "edit"}
                          />
                        </Button>
                        <Button
                          className={`icon-button ${isEditing ? "" : "vis-hidden"}`}
                          color="secondary"
                          title="Discard changes"
                          onClick={cancelEdit}
                          disabled={editIndex !== null && readOnly}
                        >
                          <FontAwesomeIcon className="whiteIcon" icon="times" />
                        </Button>
                      </ButtonGroup>
                    </td>
                    {/* )} */}
                  </>
                )}
              </tr>
            );}
          })}
        </tbody>
        <tfoot className="table-footer">
          {locationCols ? (
            <tr>
              <td colSpan={"11"}></td>
              <td className={`fw-bold`}>{helpers.formatCurrency(locationTotal)}</td>
              <td></td>
            </tr>
          ) : (
            <tr>
              <td colSpan={"9"}></td>
              <td className={`fw-bold`}>{helpers.formatCurrency(total)}</td>              
            </tr>
          )}
        </tfoot>
      </Table>
    </>
  );
}
